<template>
    <div class="grid">
        <Toast/>

        <!-- ========== Importar reporte ========== -->
        <div class="col-12" style="max-width: 1100px;">
            <div class="card">
                <h4>Conciliación de pasarelas de pago</h4>
                <p style="max-width: 720px;">
                    Sube el reporte de transacciones de tu pasarela (Datafast → Databalance, .xlsx o .csv).
                    Emparejamos cada liquidación con tus cobros con tarjeta y te mostramos lo que cuadra y lo
                    que necesita revisión. Nada se registra hasta que confirmes.
                </p>
                <div class="formgroup-inline" style="align-items: center;">
                    <div class="field">
                        <label style="width: 90px;">Pasarela</label>
                        <Dropdown v-model="acquirer" :options="acquirers" optionLabel="name" optionValue="code" style="width: 180px;"/>
                    </div>
                    <div class="field">
                        <FileUpload mode="basic" :customUpload="true" @uploader="onUpload" :auto="true"
                                    accept=".csv,.xlsx,.xls" chooseLabel="Subir reporte" class="p-button-success"/>
                    </div>
                    <i v-if="importing" class="pi pi-spin pi-spinner" style="font-size: 1.5rem;"></i>
                </div>
            </div>
        </div>

        <!-- ========== Resultado de la conciliación ========== -->
        <div class="col-12" style="max-width: 1100px;" v-if="summary">
            <div class="card">
                <h5>Resultado — {{ summary.fileName || summary.acquirer }}
                    <Tag class="ml-2" :severity="summary.status === 'CONFIRMED' ? 'success' : 'info'"
                         :value="summary.status === 'CONFIRMED' ? 'CONFIRMADO' : 'PENDIENTE'"/>
                </h5>
                <TabView>
                    <TabPanel :header="'Emparejadas (' + summary.totals.matched + ')'">
                        <DataTable :value="summary.matched" :scrollable="true" scrollHeight="400px">
                            <Column field="authorization" header="Aprobación" style="width: 110px;"></Column>
                            <Column field="cardType" header="Tarjeta" style="min-width: 150px;"></Column>
                            <Column field="trxDate" header="Fecha" style="width: 110px;"></Column>
                            <Column header="Bruto" style="width: 100px;">
                                <template #body="s">${{ s.data.gross }}</template>
                            </Column>
                            <Column header="Depositado" style="width: 110px;">
                                <template #body="s">${{ s.data.net }}</template>
                            </Column>
                            <Column header="Retenciones" style="width: 120px;">
                                <template #body="s">${{ (parseFloat(s.data.retFuente) + parseFloat(s.data.retIva)).toFixed(2) }}</template>
                            </Column>
                            <Column header="Comisión" style="width: 100px;">
                                <template #body="s">${{ s.data.commission }}</template>
                            </Column>
                            <Column field="saleId" header="Venta #" style="width: 100px;"></Column>
                            <Column header="Estado" style="width: 110px;">
                                <template #body="s">
                                    <Tag :severity="s.data.posted ? 'success' : 'warning'" :value="s.data.posted ? 'Registrada' : 'Pendiente'"/>
                                </template>
                            </Column>
                            <!-- Solo mientras no esté liquidada: el backend rechaza desemparejar una fila
                                 registrada. Es el camino que pide el aviso al anular una venta emparejada. -->
                            <Column header="" style="width: 150px;">
                                <template #body="s">
                                    <Button v-if="!s.data.posted" label="Desemparejar" icon="pi pi-times"
                                            class="p-button-sm p-button-text p-button-warning"
                                            :loading="working" @click="unmatchRow(s.data)"/>
                                </template>
                            </Column>
                        </DataTable>
                    </TabPanel>
                    <TabPanel :header="'En reporte, sin venta (' + summary.totals.unmatchedFile + ')'">
                        <Message severity="warn" :closable="false" v-if="summary.totals.unmatchedFile > 0">
                            La pasarela liquidó estas transacciones pero no encontramos el cobro en Contablus.
                            Revisa si la venta se registró con otro método de pago o no se registró.
                            Si la reconoces, <b>emparéjala a mano</b> con el cobro correspondiente —
                            hasta que lo hagas, esa liquidación no queda registrada.
                        </Message>
                        <DataTable :value="summary.unmatchedFile" :scrollable="true" scrollHeight="400px">
                            <Column field="authorization" header="Aprobación" style="width: 110px;"></Column>
                            <Column field="cardType" header="Tarjeta" style="min-width: 130px;"></Column>
                            <Column field="trxDate" header="Fecha" style="width: 110px;"></Column>
                            <Column header="Bruto" style="width: 100px;">
                                <template #body="s">${{ s.data.gross }}</template>
                            </Column>
                            <Column header="Depositado" style="width: 110px;">
                                <template #body="s">${{ s.data.net }}</template>
                            </Column>
                            <Column header="Emparejar con" style="min-width: 300px;">
                                <template #body="s">
                                    <Dropdown v-model="pairing[s.data.id]" :options="candidates(s.data)"
                                              optionLabel="label" optionValue="transactionId"
                                              placeholder="Elige el cobro" style="width: 190px;"/>
                                    <Button label="Unir" class="p-button-sm ml-2"
                                            :disabled="!pairing[s.data.id]" :loading="working"
                                            @click="matchRow(s.data)"/>
                                </template>
                            </Column>
                        </DataTable>
                    </TabPanel>
                    <TabPanel :header="'En sistema, sin liquidar (' + summary.totals.unmatchedSystem + ')'">
                        <Message severity="info" :closable="false" v-if="summary.totals.unmatchedSystem > 0">
                            Estos cobros con tarjeta no aparecen liquidados en el reporte (aún en tránsito, o de otra fecha).
                        </Message>
                        <DataTable :value="summary.unmatchedSystem" :scrollable="true" scrollHeight="400px">
                            <Column field="approvalNumber" header="Aprobación" style="width: 120px;"></Column>
                            <Column field="saleId" header="Venta #" style="width: 100px;"></Column>
                            <Column field="date" header="Fecha" style="width: 110px;"></Column>
                            <Column header="Monto" style="width: 110px;">
                                <template #body="s">${{ s.data.amount }}</template>
                            </Column>
                        </DataTable>
                    </TabPanel>
                </TabView>

                <Divider/>
                <div class="formgroup-inline" style="align-items: center;" v-if="hasPendingMatched">
                    <div class="field">
                        <label style="width: 160px;">Depositado en la cuenta</label>
                        <Dropdown v-model="selectedBankId" :options="bankAccounts" optionLabel="name" optionValue="id"
                                  placeholder="-- Cuenta bancaria --" style="width: 240px;"/>
                    </div>
                    <Button label="Confirmar liquidación" icon="pi pi-check" class="p-button-success mb-2"
                            :loading="confirming" :disabled="!selectedBankId" @click="confirm"/>
                    <small style="color: #777;">Registra los depósitos, comisiones y retenciones de las emparejadas pendientes.</small>
                </div>
                <Message v-else-if="summary.totals.matched > 0" severity="success" :closable="false">
                    Todas las transacciones emparejadas de este reporte ya fueron registradas.
                </Message>
            </div>
        </div>

        <!-- ========== Historial ========== -->
        <div class="col-12" style="max-width: 1100px;">
            <div class="card">
                <h5>Historial de importaciones</h5>
                <DataTable :value="batches" :loading="loadingBatches" selectionMode="single" @rowSelect="onBatchSelected">
                    <Column field="batchId" header="#" style="width: 80px;"></Column>
                    <Column field="acquirer" header="Pasarela" style="width: 130px;"></Column>
                    <Column field="fileName" header="Archivo"></Column>
                    <Column header="Estado" style="width: 130px;">
                        <template #body="s">
                            <Tag :severity="s.data.status === 'CONFIRMED' ? 'success' : 'info'"
                                 :value="s.data.status === 'CONFIRMED' ? 'Confirmado' : 'Pendiente'"/>
                        </template>
                    </Column>
                    <Column field="createdAt" header="Importado" style="width: 180px;"></Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';

export default {
    data() {
        return {
            acquirer: 'DATAFAST',
            // Fallback mientras carga el catálogo (o si el endpoint falla): importar
            // es lo que el usuario vino a hacer, así que el dropdown nunca queda vacío.
            acquirers: [{ code: 'DATAFAST', name: 'Datafast' }],
            importing: false,
            confirming: false,
            loadingBatches: false,
            working: false,
            summary: null,
            pairing: {},   // { rowId: transactionId } elegido a mano
            batches: [],
            selectedBankId: null,
        }
    },
    computed: {
        bankAccounts() {
            const accounts = this.$store.getters.finantialAccounts || [];
            // El depósito llega a una cuenta bancaria (no efectivo ni la de tarjetas).
            return accounts.filter((a) => a.moneyType === 'TRANSFER');
        },
        hasPendingMatched() {
            if (!this.summary) return false;
            return this.summary.matched.some((r) => !r.posted);
        },
    },
    mounted() {
        this.loadAcquirers();
        this.loadBatches();
    },
    methods: {
        async loadAcquirers() {
            // Mismo catálogo que el dropdown de la cuenta CARD, servido por el core:
            // lo que se puede elegir acá es exactamente lo que sabemos liquidar.
            try {
                const response = await httpRequest.getPaymentGateways();
                if (response.status === 200 && response.data && response.data.length) {
                    this.acquirers = response.data;
                }
            } catch (error) {
                console.error('Error al cargar pasarelas:', error);
            }
        },
        candidates(row) {
            // Las dos listas sin emparejar son candidatas la una de la otra. Solo se
            // ofrecen los cobros del mismo monto: si difiere casi seguro no es la
            // misma transaccion, y el backend lo rechaza de todos modos.
            return (this.summary.unmatchedSystem || [])
                .filter(fat => parseFloat(fat.amount) === parseFloat(row.gross))
                .map(fat => ({
                    transactionId: fat.transactionId,
                    label: 'Venta #' + (fat.saleId || '—') + ' · ' + fat.date
                           + ' · $' + fat.amount,
                }));
        },
        async matchRow(row) {
            this.working = true;
            const response = await httpRequest.matchSettlementRow(
                row.id, this.pairing[row.id]);
            this.working = false;
            if (response.status === 200) {
                this.$toast.add({
                    severity: 'success', summary: 'Emparejada',
                    detail: response.data.posted
                        ? 'Se registró su liquidación'
                        : 'Se liquidará al confirmar el reporte',
                    life: 4000});
                this.pairing = {};
                await this.reloadSummary();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo emparejar',
                                 detail: response.error || '', life: 6000});
            }
        },
        async unmatchRow(row) {
            this.working = true;
            const response = await httpRequest.unmatchSettlementRow(row.id);
            this.working = false;
            if (response.status === 200) {
                this.$toast.add({
                    severity: 'success', summary: 'Desemparejada',
                    detail: 'La fila volvió a «En reporte, sin venta».', life: 4000});
                await this.reloadSummary();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo desemparejar',
                                 detail: response.error || '', life: 6000});
            }
        },
        async onUpload(event) {
            const file = event.files && event.files[0];
            if (!file) return;
            this.importing = true;
            const response = await httpRequest.importSettlement(file, this.acquirer);
            this.importing = false;
            if (response.status === 200) {
                this.summary = response.data;
                this.$toast.add({severity: 'success', summary: 'Reporte procesado',
                    detail: `${response.data.totals.matched} emparejadas, ${response.data.totals.unmatchedFile + response.data.totals.unmatchedSystem} por revisar`, life: 5000});
                this.loadBatches();
            } else {
                this.$toast.add({severity: 'error', summary: 'Error', detail: response.error || 'No se pudo procesar el reporte', life: 5000});
            }
        },
        async confirm() {
            this.confirming = true;
            const response = await httpRequest.confirmSettlement(this.summary.batchId, this.selectedBankId);
            this.confirming = false;
            if (response.status === 200) {
                this.summary = response.data;
                const outcome = response.data.outcome || {};
                this.$toast.add({severity: 'success', summary: 'Liquidación registrada',
                    detail: `${outcome.posted || 0} transacción(es) registradas en tesorería y contabilidad`, life: 5000});
                this.loadBatches();
            } else {
                this.$toast.add({severity: 'error', summary: 'Error', detail: response.error || 'No se pudo confirmar', life: 5000});
            }
        },
        async loadBatches() {
            this.loadingBatches = true;
            const response = await httpRequest.getSettlementBatches();
            this.loadingBatches = false;
            if (response.status === 200) this.batches = response.data;
        },
        async reloadSummary() {
            if (!this.summary) return;
            const response = await httpRequest.getSettlementBatches(this.summary.batchId);
            if (response.status === 200) this.summary = response.data;
        },
        async onBatchSelected(event) {
            const response = await httpRequest.getSettlementBatches(event.data.batchId);
            if (response.status === 200) this.summary = response.data;
        },
    },
}
</script>
